/** @format */

/**
 * Decoupled table operations — a `TableProvider` (React Context keyed by a unique
 * `tableId`) plus standalone control sub-components that can be placed ANYWHERE
 * on the page and still drive the table. Per-table preferences (density, frame,
 * hidden columns) persist in localStorage by `tableId`. No bespoke panel — these
 * are reusable common components you compose where you like.
 *
 *   <TableProvider tableId="leads" columns={cols}>
 *     <div className="flex gap-2"><TableSearch /><TableDensity /><TableFrameToggle /><TableColumns /></div>
 *     <TableView data={rows} columns={cols} rowKey={r => r.id} searchAccessor={r => r.name} />
 *   </TableProvider>
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Search,
  LayoutGrid,
  Square,
  Plus,
  Settings2,
  GripVertical,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Check,
} from 'lucide-react';
import {
  Button,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Switch,
  cn,
} from '@clarix/ui';
import { DataTable, type DataTableColumn, type DataTableProps } from './DataTable';
import type { Density } from './engine';

type Frame = 'canvas' | 'bordered';

/** A toggleable column for the Fields/Columns control. */
export interface TableColumnMeta {
  id: string;
  label: ReactNode;
  /** Optional field-type icon shown in the Fields panel (e.g. <Calendar/>). */
  icon?: ReactNode;
}

/** A selectable view (e.g. list / board) shown in the settings gear. */
export interface TableViewOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

type TableAlign = 'left' | 'center' | 'right';

interface TablePrefs {
  density: Density;
  frame: Frame;
  hiddenColumns: string[];
  /** Active view id (e.g. 'list' | 'board'); resolved from `views` when unset. */
  view?: string;
  /** Table-wide column alignment (applies to header + content together). */
  align?: TableAlign;
}

const DEFAULT_PREFS: TablePrefs = { density: 'comfortable', frame: 'canvas', hiddenColumns: [] };
const PREFS_PREFIX = 'crm:datatable:prefs:';

/** Coerce a loosely-typed prefs blob (localStorage / API) into a valid TablePrefs. */
function normalizePrefs(p: Partial<TablePrefs> | null | undefined): TablePrefs {
  if (!p) return DEFAULT_PREFS;
  return {
    density: p.density ?? DEFAULT_PREFS.density,
    frame: p.frame ?? DEFAULT_PREFS.frame,
    hiddenColumns: Array.isArray(p.hiddenColumns) ? p.hiddenColumns : [],
    view: typeof p.view === 'string' ? p.view : undefined,
    align: p.align === 'center' || p.align === 'right' || p.align === 'left' ? p.align : undefined,
  };
}

/** Read the localStorage cache, returning `null` when nothing is stored. */
function readPrefsRaw(tableId: string): TablePrefs | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(PREFS_PREFIX + tableId);
    if (!raw) return null;
    return normalizePrefs(JSON.parse(raw) as Partial<TablePrefs>);
  } catch {
    return null;
  }
}

/** Initial prefs for first paint — localStorage cache, else defaults. */
function readPrefs(tableId: string): TablePrefs {
  return readPrefsRaw(tableId) ?? DEFAULT_PREFS;
}

/** Write the localStorage cache (always kept in sync for instant next load). */
function writePrefsCache(tableId: string, prefs: TablePrefs): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PREFS_PREFIX + tableId, JSON.stringify(prefs));
  } catch {
    /* storage disabled — keep prefs in-memory */
  }
}

/**
 * Pluggable persistence target for table prefs. ui-shared NEVER imports an app's
 * API client (CLAUDE.md rule); instead the app injects a `store` backed by the
 * user-preference API. When no `store` is passed, prefs persist to localStorage
 * only (the built-in cache). With a `store`, the server is the source of truth:
 * `load` hydrates on mount, `save` (debounced) persists changes — and the
 * localStorage cache is still kept for instant first paint.
 */
export interface TablePrefsStore {
  /** May return a partial blob (e.g. from the API) — the provider normalizes it. */
  load(tableId: string): Partial<TablePrefs> | null | Promise<Partial<TablePrefs> | null>;
  save(tableId: string, prefs: TablePrefs): void | Promise<void>;
}

interface TableControlsValue {
  tableId: string;
  prefs: TablePrefs;
  search: string;
  columns: TableColumnMeta[];
  views: TableViewOption[];
  /** Active view id — falls back to the first configured view. */
  view: string | undefined;
  setDensity: (d: Density) => void;
  setFrame: (f: Frame) => void;
  setSearch: (s: string) => void;
  setView: (v: string) => void;
  setAlign: (a: TableAlign) => void;
  toggleColumn: (id: string) => void;
  /** Shared open state for the Fields drawer, so any trigger can open it. */
  fieldsOpen: boolean;
  setFieldsOpen: (open: boolean) => void;
}

const TableControlsContext = createContext<TableControlsValue | null>(null);

/** Read the controls for the surrounding table. Throws if used outside a provider. */
export function useTableControls(): TableControlsValue {
  const ctx = useContext(TableControlsContext);
  if (!ctx) throw new Error('useTableControls must be used within a <TableProvider>');
  return ctx;
}

/** Debounce window for persisting prefs to the injected store (server). */
const SAVE_DEBOUNCE_MS = 600;

export function TableProvider({
  tableId,
  columns,
  views = [],
  store,
  children,
}: {
  tableId: string;
  /** Toggleable columns (id + label + optional icon) for the Fields control. */
  columns: TableColumnMeta[];
  /** Optional view options (e.g. list/board) shown inside the settings gear. */
  views?: TableViewOption[];
  /**
   * Optional persistence target (e.g. backed by the user-preference API). When
   * omitted, prefs persist to localStorage only. When provided, the server is the
   * source of truth (hydrated on mount, saved debounced) and localStorage is kept
   * as a fast first-paint cache.
   */
  store?: TablePrefsStore;
  children: ReactNode;
}) {
  // First paint = localStorage cache (instant, no flash of default columns).
  const [prefs, setPrefs] = useState<TablePrefs>(() => readPrefs(tableId));
  const [search, setSearch] = useState('');
  const [fieldsOpen, setFieldsOpen] = useState(false);

  // True once the user has changed prefs this session — guards against a late
  // server `load` clobbering fresh local edits.
  const dirtyRef = useRef(false);
  const markDirty = useCallback(() => {
    dirtyRef.current = true;
  }, []);

  // Hydrate from the store (server) on mount / tableId change. Server wins unless
  // the user already touched prefs locally. Stale-guard via a cancelled flag.
  useEffect(() => {
    dirtyRef.current = false;
    if (!store) return;
    let cancelled = false;
    void Promise.resolve(store.load(tableId)).then((loaded) => {
      if (cancelled || !loaded || dirtyRef.current) return;
      setPrefs(normalizePrefs(loaded));
    });
    return () => {
      cancelled = true;
    };
  }, [tableId, store]);

  // Persist on change: always mirror to the localStorage cache (sync), and
  // debounce the store (server) save. Skipped on the very first render.
  const firstRender = useRef(true);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    writePrefsCache(tableId, prefs);
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (!store) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void Promise.resolve(store.save(tableId, prefs)).catch(() => {
        /* offline / error — localStorage cache already holds the value */
      });
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [tableId, prefs, store]);

  const view = prefs.view ?? views[0]?.value;

  const value = useMemo<TableControlsValue>(
    () => ({
      tableId,
      prefs,
      search,
      columns,
      views,
      view,
      fieldsOpen,
      setFieldsOpen,
      setDensity: (density) => {
        markDirty();
        setPrefs((p) => ({ ...p, density }));
      },
      setFrame: (frame) => {
        markDirty();
        setPrefs((p) => ({ ...p, frame }));
      },
      setSearch,
      setView: (v) => {
        markDirty();
        setPrefs((p) => ({ ...p, view: v }));
      },
      setAlign: (align) => {
        markDirty();
        setPrefs((p) => ({ ...p, align }));
      },
      toggleColumn: (id) => {
        markDirty();
        setPrefs((p) => ({
          ...p,
          hiddenColumns: p.hiddenColumns.includes(id)
            ? p.hiddenColumns.filter((c) => c !== id)
            : [...p.hiddenColumns, id],
        }));
      },
    }),
    [tableId, prefs, search, columns, views, view, fieldsOpen, markDirty],
  );

  return <TableControlsContext.Provider value={value}>{children}</TableControlsContext.Provider>;
}

/* ───────────────────────── Control sub-components ───────────────────────── */

/** Minimal search: a search icon that expands into a slim input on demand. */
export function TableSearch({ placeholder = 'Search…', className }: { placeholder?: string; className?: string }) {
  const { search, setSearch } = useTableControls();
  const [open, setOpen] = useState(search.length > 0);

  if (!open) {
    return (
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={cn('h-8 w-8', className)}
        aria-label="Search"
        onClick={() => setOpen(true)}
      >
        <Search className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <label className={cn('relative inline-flex items-center', className)}>
      <Search className="text-muted-foreground absolute left-2 h-3.5 w-3.5" />
      <Input
        autoFocus
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onBlur={() => {
          if (!search) setOpen(false);
        }}
        placeholder={placeholder}
        className="h-8 w-44 pl-7 text-xl"
      />
    </label>
  );
}

export function TableDensityToggle({ className }: { className?: string }) {
  const { prefs, setDensity } = useTableControls();
  const options: Density[] = ['compact', 'comfortable', 'spacious'];
  return (
    <div className={cn('inline-flex items-center gap-1', className)}>
      {options.map((d) => (
        <Button
          key={d}
          type="button"
          size="sm"
          variant={prefs.density === d ? 'default' : 'outline'}
          className="h-8 capitalize"
          onClick={() => setDensity(d)}
        >
          {d}
        </Button>
      ))}
    </div>
  );
}

/**
 * Row-density control as a SINGLE button → popover with the three options
 * (Compact / Comfortable / Spacious), instead of the inline 3-button toggle.
 */
export function TableDensityMenu({ className }: { className?: string }) {
  const { prefs, setDensity } = useTableControls();
  const options: Density[] = ['compact', 'comfortable', 'spacious'];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={cn('h-8 w-8', className)}
          aria-label="Row density"
          title="Row density"
        >
          <Settings2 className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-40 p-1">
        <p className="text-muted-foreground px-2 pb-1 pt-1 text-sm font-medium uppercase">Density</p>
        {options.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDensity(d)}
            className={cn(
              'hover:bg-muted flex w-full items-center justify-between rounded-md px-2 py-1.5 text-md capitalize',
              prefs.density === d && 'font-medium',
            )}
          >
            {d}
            <Check
              className={cn('text-primary h-3.5 w-3.5 shrink-0', prefs.density === d ? 'opacity-100' : 'opacity-0')}
            />
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function TableFrameToggle({ className }: { className?: string }) {
  const { prefs, setFrame } = useTableControls();
  return (
    <div className={cn('inline-flex items-center gap-1', className)}>
      <Button
        type="button"
        size="sm"
        variant={prefs.frame === 'canvas' ? 'default' : 'outline'}
        className="h-8"
        aria-label="Canvas"
        onClick={() => setFrame('canvas')}
      >
        <LayoutGrid className="h-3.5 w-3.5" /> Canvas
      </Button>
      <Button
        type="button"
        size="sm"
        variant={prefs.frame === 'bordered' ? 'default' : 'outline'}
        className="h-8"
        aria-label="Bordered"
        onClick={() => setFrame('bordered')}
      >
        <Square className="h-3.5 w-3.5" /> Border
      </Button>
    </div>
  );
}

/** One field row in the Fields panel — drag handle + type icon + label + toggle. */
function FieldRow({
  field,
  checked,
  onToggle,
}: {
  field: TableColumnMeta;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="group hover:bg-muted/60 flex items-center gap-1.5 rounded-md px-1.5 py-1">
      <GripVertical className="text-muted-foreground/40 h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
      {field.icon ? (
        <span className="text-muted-foreground flex h-3.5 w-3.5 shrink-0 items-center justify-center [&_svg]:h-3.5 [&_svg]:w-3.5">
          {field.icon}
        </span>
      ) : null}
      <span className="flex-1 truncate text-lg">{field.label}</span>
      <Switch size="xs" checked={checked} onCheckedChange={onToggle} aria-label={`Toggle ${String(field.label)}`} />
    </div>
  );
}

/** Fields panel — the "+" control. Search + grouped Shown/Hidden field toggles. */
export function TableColumns({ className }: { className?: string }) {
  const { columns, prefs, toggleColumn } = useTableControls();
  const [q, setQ] = useState('');

  const query = q.trim().toLowerCase();
  const match = (c: TableColumnMeta) => !query || String(c.label).toLowerCase().includes(query);
  const shown = columns.filter((c) => match(c) && !prefs.hiddenColumns.includes(c.id));
  const hidden = columns.filter((c) => match(c) && prefs.hiddenColumns.includes(c.id));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className={cn('h-6 w-6 rounded-md', className)}
          aria-label="Fields"
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="flex items-center px-3 pt-3 pb-2">
          <span className="text-xl font-semibold">Fields</span>
        </div>
        <div className="px-3 pb-2">
          <label className="relative flex items-center">
            <Search className="text-muted-foreground absolute left-2.5 h-3.5 w-3.5" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search fields…"
              className="h-9 pl-8 text-xl"
            />
          </label>
        </div>
        <div className="max-h-72 overflow-auto px-1.5 pb-2">
          <div className="text-muted-foreground flex items-center justify-between px-1.5 pt-1 pb-0.5 text-sm font-medium uppercase tracking-wide">
            <span>Shown</span>
            <span>{shown.length}</span>
          </div>
          {shown.map((c) => (
            <FieldRow key={c.id} field={c} checked onToggle={() => toggleColumn(c.id)} />
          ))}
          {hidden.length > 0 ? (
            <>
              <div className="text-muted-foreground flex items-center justify-between px-1.5 pt-2 pb-0.5 text-sm font-medium uppercase tracking-wide">
                <span>Hidden</span>
                <span>{hidden.length}</span>
              </div>
              {hidden.map((c) => (
                <FieldRow key={c.id} field={c} checked={false} onToggle={() => toggleColumn(c.id)} />
              ))}
            </>
          ) : null}
          {shown.length === 0 && hidden.length === 0 ? (
            <p className="text-muted-foreground px-2 py-4 text-center text-lg">No fields found.</p>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Fields control — a compact "Toggle columns" POPOVER (the "+" trigger). Lists
 * every column with a leading check when shown; clicking toggles visibility.
 * Consumes `useTableControls()` (no props/prop-drilling); open state is the
 * shared `fieldsOpen` so any trigger can drive it.
 */
export function TableFieldsDrawer({ className }: { className?: string }) {
  const { columns, prefs, toggleColumn, fieldsOpen, setFieldsOpen } = useTableControls();
  return (
    <Popover open={fieldsOpen} onOpenChange={setFieldsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className={cn('h-6 w-6 rounded-md', className)}
          aria-label="Toggle columns"
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-44 p-1">
        <div className="text-muted-foreground px-2 pb-1 pt-1.5 text-sm font-semibold uppercase tracking-wide">
          Toggle columns
        </div>
        <div className="max-h-72 overflow-auto">
          {columns.map((c) => {
            const checked = !prefs.hiddenColumns.includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggleColumn(c.id)}
                className="hover:bg-muted/60 flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xl transition-colors"
                role="menuitemcheckbox"
                aria-checked={checked}
              >
                <Check className={cn('text-primary h-4 w-4 shrink-0', checked ? 'opacity-100' : 'opacity-0')} />
                {c.icon ? <span className="text-muted-foreground [&_svg]:h-3.5 [&_svg]:w-3.5">{c.icon}</span> : null}
                <span className="flex-1 truncate">{c.label}</span>
              </button>
            );
          })}
          {columns.length === 0 ? (
            <p className="text-muted-foreground px-2 py-4 text-center text-lg">No fields.</p>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Settings gear → popover holding the less-frequent controls (view + frame + density). */
export function TableSettings({ className }: { className?: string }) {
  const { prefs, views, view, setView, setFrame, setDensity, setAlign } = useTableControls();
  const densities: Density[] = ['compact', 'comfortable', 'spacious'];
  const aligns: { value: TableAlign; icon: ReactNode }[] = [
    { value: 'left', icon: <AlignLeft className="h-3.5 w-3.5" /> },
    { value: 'center', icon: <AlignCenter className="h-3.5 w-3.5" /> },
    { value: 'right', icon: <AlignRight className="h-3.5 w-3.5" /> },
  ];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={cn('h-8 w-8', className)}
          aria-label="Table settings"
        >
          <Settings2 className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 space-y-3 p-2">
        {views.length > 0 ? (
          <div>
            <p className="text-muted-foreground mb-1 px-1 text-sm font-medium uppercase">View</p>
            <div className="grid grid-cols-2 gap-1">
              {views.map((v) => (
                <Button
                  key={v.value}
                  type="button"
                  size="sm"
                  variant={view === v.value ? 'default' : 'outline'}
                  className="h-7 gap-1 text-md capitalize"
                  onClick={() => setView(v.value)}
                >
                  {v.icon}
                  {v.label}
                </Button>
              ))}
            </div>
          </div>
        ) : null}
        <div>
          <p className="text-muted-foreground mb-1 px-1 text-sm font-medium uppercase">Frame</p>
          <div className="grid grid-cols-2 gap-1">
            {(['canvas', 'bordered'] as const).map((f) => (
              <Button
                key={f}
                type="button"
                size="sm"
                variant={prefs.frame === f ? 'default' : 'outline'}
                className="h-7 gap-1 text-md capitalize"
                onClick={() => setFrame(f)}
              >
                {f === 'canvas' ? <LayoutGrid className="h-3 w-3" /> : <Square className="h-3 w-3" />}
                {f}
              </Button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-muted-foreground mb-1 px-1 text-sm font-medium uppercase">Density</p>
          <div className="grid grid-cols-3 gap-1">
            {densities.map((d) => (
              <Button
                key={d}
                type="button"
                size="sm"
                variant={prefs.density === d ? 'default' : 'outline'}
                className="h-7 text-sm capitalize"
                onClick={() => setDensity(d)}
              >
                {d}
              </Button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-muted-foreground mb-1 px-1 text-sm font-medium uppercase">Alignment</p>
          <div className="grid grid-cols-3 gap-1">
            {aligns.map((a) => (
              <Button
                key={a.value}
                type="button"
                size="sm"
                variant={(prefs.align ?? 'left') === a.value ? 'default' : 'outline'}
                className="h-7"
                aria-label={`Align ${a.value}`}
                onClick={() => setAlign(a.value)}
              >
                {a.icon}
              </Button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Minimal reusable Table Operations Panel — slim search + a settings gear
 * (view/frame/density/align). The Fields/columns control (`<TableColumns/>`)
 * lives in the table's trailing "+" slot, not here. Drop it ANYWHERE inside a
 * <TableProvider>; it drives the table by `tableId` through context.
 */
export function TableToolbar({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center gap-0.5', className)}>
      <TableSearch />
      <span className="flex-1" />
      <TableSettings />
    </div>
  );
}

/** Alias for {@link TableToolbar} — the "Table Operations Panel". */
export const TableOperationsPanel = TableToolbar;

/* ───────────────────────── Bound table view ───────────────────────── */

type TableViewProps<T> = Omit<DataTableProps<T>, 'density' | 'variant' | 'persistKey'> & {
  /** Optional row→text accessor so <TableSearch> filters the rows. */
  searchAccessor?: (row: T) => string;
};

/** Renders the shared DataTable wired to the surrounding provider's prefs. */
export function TableView<T>({ data, columns, searchAccessor, ...rest }: TableViewProps<T>) {
  const { tableId, prefs, search } = useTableControls();
  const allRows = data ?? [];

  const visibleColumns = useMemo(
    () => columns.filter((c) => !prefs.hiddenColumns.includes(c.id)),
    [columns, prefs.hiddenColumns],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || !searchAccessor) return allRows;
    return allRows.filter((r) => searchAccessor(r).toLowerCase().includes(q));
  }, [allRows, search, searchAccessor]);

  return (
    <DataTable<T>
      {...(rest as DataTableProps<T>)}
      data={rows}
      columns={visibleColumns as DataTableColumn<T>[]}
      density={prefs.density}
      variant={prefs.frame === 'bordered' ? 'boxy' : 'canvas'}
      persistKey={tableId}
      hideToolbar
    />
  );
}
